import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PageLayout } from '@voilajsx/uikit/page';
import { Card, CardContent } from '@voilajsx/uikit/card';
import { Button } from '@voilajsx/uikit/button';
import { Badge } from '@voilajsx/uikit/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@voilajsx/uikit/tabs';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@voilajsx/uikit/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@voilajsx/uikit/dialog';
import { Alert, AlertDescription } from '@voilajsx/uikit/alert';
import { Input } from '@voilajsx/uikit/input';
import { Loader2, AlertTriangle, Trash2, ExternalLink, ChevronLeft, ChevronRight, FileText, Download, Search } from 'lucide-react';
import { Header,  SEO } from '../../../shared/components';
import { useAuth } from '../../auth';
import { config } from '../../auth/config';

interface Design {
  id: string;
  name: string;
  serviceCode: string;
  serviceName?: string;
  downloadCount: number;
  createdAt: string;
  updatedAt: string;
}

interface DownloadEntry {
  id: string;
  designId: string;
  designName: string;
  serviceCode: string;
  serviceName?: string;
  tier: string;
  creditsCharged: number;
  createdAt: string;
}

const ActivityPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, token } = useAuth();

  // Designs state
  const [designs, setDesigns] = useState<Design[]>([]);
  const [designsPage, setDesignsPage] = useState(1);
  const [designsTotalPages, setDesignsTotalPages] = useState(1);
  const [designsLoading, setDesignsLoading] = useState(true);
  const [designsError, setDesignsError] = useState<string | null>(null);

  // Downloads state
  const [downloads, setDownloads] = useState<DownloadEntry[]>([]);
  const [downloadsPage, setDownloadsPage] = useState(1);
  const [downloadsTotalPages, setDownloadsTotalPages] = useState(1);
  const [downloadsLoading, setDownloadsLoading] = useState(true);
  const [downloadsError, setDownloadsError] = useState<string | null>(null);

  // Search
  const [searchTerm, setSearchTerm] = useState('');

  // Delete dialog state
  const [deleteTarget, setDeleteTarget] = useState<Design | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const pageSize = 20;

  const authHeaders = useCallback(() => ({
    'Content-Type': 'application/json',
    [config.auth.headers.frontendKey]: config.auth.headers.frontendKeyValue,
    [config.auth.headers.auth]: `Bearer ${token}`,
  }), [token]);

  const fetchDesigns = useCallback(async (page: number) => {
    if (!isAuthenticated || !token) return;

    setDesignsLoading(true);
    setDesignsError(null);

    try {
      const res = await fetch(`${config.api.baseUrl}/api/design/list?page=${page}&pageSize=${pageSize}`, {
        headers: authHeaders()
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);

      const json = await res.json();
      const data = json.data || json;
      setDesigns(data?.designs || []);
      setDesignsTotalPages(data?.totalPages || data?.pagination?.totalPages || 1);
    } catch (err: any) {
      setDesignsError(err.message || 'Failed to load designs');
    } finally {
      setDesignsLoading(false);
    }
  }, [isAuthenticated, token, authHeaders]);

  const fetchDownloads = useCallback(async (page: number) => {
    if (!isAuthenticated || !token) return;

    setDownloadsLoading(true);
    setDownloadsError(null);

    try {
      const res = await fetch(`${config.api.baseUrl}/api/download/history?page=${page}&pageSize=${pageSize}`, {
        headers: authHeaders()
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);

      const json = await res.json();
      const data = json.data || json;
      setDownloads(data?.downloads || []);
      setDownloadsTotalPages(data?.totalPages || data?.pagination?.totalPages || 1);
    } catch (err: any) {
      setDownloadsError(err.message || 'Failed to load download history');
    } finally {
      setDownloadsLoading(false);
    }
  }, [isAuthenticated, token, authHeaders]);

  useEffect(() => {
    fetchDesigns(designsPage);
  }, [designsPage, fetchDesigns]);

  useEffect(() => {
    fetchDownloads(downloadsPage);
  }, [downloadsPage, fetchDownloads]);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const searchLower = searchTerm.toLowerCase();
  const filteredDesigns = searchTerm
    ? designs.filter(d => d.name.toLowerCase().includes(searchLower) || d.serviceCode.toLowerCase().includes(searchLower))
    : designs;
  const filteredDownloads = searchTerm
    ? downloads.filter(d => d.designName.toLowerCase().includes(searchLower) || d.serviceCode.toLowerCase().includes(searchLower))
    : downloads;

  const handleDeleteClick = (design: Design) => {
    setDeleteTarget(design);
    setDeleteError(null);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget || !token) return;

    setDeleting(true);
    setDeleteError(null);

    try {
      const res = await fetch(`${config.api.baseUrl}/api/design/${deleteTarget.id}`, {
        method: 'DELETE',
        headers: authHeaders()
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || `HTTP ${res.status}: ${res.statusText}`);
      }

      // Remove from local state
      setDesigns((prev) => prev.filter((d) => d.id !== deleteTarget.id));
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
    } catch (err: any) {
      setDeleteError(err.message || 'Failed to delete design');
    } finally {
      setDeleting(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <PageLayout size="full">
        <SEO title="My Activity" description="View your designs and download history" />
        <Header />
        <PageLayout.Content>
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Please{' '}
              <Link to="/auth/login" className="font-medium text-primary hover:underline">
                sign in
              </Link>{' '}
              to view your activity.
            </AlertDescription>
          </Alert>
        </PageLayout.Content>
      </PageLayout>
    );
  }

  return (
    <PageLayout size="full">
      <SEO
        title="My Activity"
        description="View your designs and download history"
      />
      <Header />

      <PageLayout.Content>
        <div className="space-y-6 pb-20">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">My Activity</h1>
            <p className="text-muted-foreground">
              Manage your designs and view download history
            </p>
          </div>

          <Tabs defaultValue="designs">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <TabsList>
                <TabsTrigger value="designs">
                  <FileText className="h-4 w-4 mr-2" />
                  My Designs
                </TabsTrigger>
                <TabsTrigger value="downloads">
                  <Download className="h-4 w-4 mr-2" />
                  Download History
                </TabsTrigger>
              </TabsList>
              <div className="relative" style={{ width: 260 }}>
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  style={{ height: 34, fontSize: 13 }}
                />
              </div>
            </div>

            {/* My Designs Tab */}
            <TabsContent value="designs">
              <Card className="border-zinc-700">
                <CardContent className="pt-6">
                  {designsError && (
                    <Alert className="bg-destructive/10 border-destructive text-destructive mb-4">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>{designsError}</AlertDescription>
                    </Alert>
                  )}

                  {designsLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                      <span className="ml-2 text-muted-foreground">Loading designs...</span>
                    </div>
                  ) : filteredDesigns.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 gap-4">
                      <FileText className="h-12 w-12 text-muted-foreground/50" />
                      <p className="text-muted-foreground text-lg">{searchTerm ? 'No designs match your search' : 'No designs yet. Start creating!'}</p>
                    </div>
                  ) : (
                    <>
                      <div className="overflow-x-auto rounded-md border border-zinc-700">
                        <Table>
                          <TableHeader>
                            <TableRow className="border-b border-zinc-700">
                              <TableHead className="border-r border-zinc-700">Name</TableHead>
                              <TableHead className="border-r border-zinc-700">Service Type</TableHead>
                              <TableHead className="border-r border-zinc-700 text-center">Downloads</TableHead>
                              <TableHead className="border-r border-zinc-700">Created</TableHead>
                              <TableHead className="border-r border-zinc-700">Last Updated</TableHead>
                              <TableHead>Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {filteredDesigns.map((design) => (
                              <TableRow key={design.id} className="border-b border-zinc-700">
                                <TableCell className="border-r border-zinc-700 font-medium">
                                  <a
                                    href={`/services/generator?designId=${design.id}`}
                                    onClick={(e) => { e.preventDefault(); navigate(`/services/generator?designId=${design.id}`); }}
                                    style={{ color: '#FFD93D', textDecoration: 'none', cursor: 'pointer' }}
                                    onMouseEnter={e => { e.currentTarget.style.textDecoration = 'underline'; }}
                                    onMouseLeave={e => { e.currentTarget.style.textDecoration = 'none'; }}
                                  >
                                    {design.name}
                                  </a>
                                </TableCell>
                                <TableCell className="border-r border-zinc-700">
                                  <Badge variant="secondary">
                                    {design.serviceName || design.serviceCode}
                                  </Badge>
                                </TableCell>
                                <TableCell className="border-r border-zinc-700 text-center">
                                  {design.downloadCount}
                                </TableCell>
                                <TableCell className="border-r border-zinc-700 text-sm text-muted-foreground whitespace-nowrap">
                                  {formatDate(design.createdAt)}
                                </TableCell>
                                <TableCell className="border-r border-zinc-700 text-sm text-muted-foreground whitespace-nowrap">
                                  {formatDate(design.updatedAt)}
                                </TableCell>
                                <TableCell>
                                  <div className="flex items-center gap-2">
                                    <button
                                      onClick={() => navigate(`/services/generator?designId=${design.id}`)}
                                      style={{
                                        display: 'inline-flex', alignItems: 'center', gap: 6,
                                        padding: '5px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                                        background: 'rgba(255,217,61,0.1)', border: '1px solid rgba(255,217,61,0.3)',
                                        color: '#FFD93D', cursor: 'pointer',
                                      }}
                                    >
                                      <ExternalLink size={13} /> Open
                                    </button>
                                    <button
                                      onClick={() => handleDeleteClick(design)}
                                      style={{
                                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                        width: 30, height: 30, borderRadius: 6,
                                        background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)',
                                        color: '#ef4444', cursor: 'pointer',
                                      }}
                                    >
                                      <Trash2 size={13} />
                                    </button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>

                      {/* Pagination */}
                      <div className="flex items-center justify-between pt-4">
                        <p className="text-sm text-muted-foreground">
                          Page {designsPage} of {designsTotalPages}
                        </p>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setDesignsPage((p) => Math.max(1, p - 1))}
                            disabled={designsPage <= 1}
                          >
                            <ChevronLeft className="h-4 w-4 mr-1" />
                            Previous
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setDesignsPage((p) => Math.min(designsTotalPages, p + 1))}
                            disabled={designsPage >= designsTotalPages}
                          >
                            Next
                            <ChevronRight className="h-4 w-4 ml-1" />
                          </Button>
                        </div>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Download History Tab */}
            <TabsContent value="downloads">
              <Card className="border-zinc-700">
                <CardContent className="pt-6">
                  {downloadsError && (
                    <Alert className="bg-destructive/10 border-destructive text-destructive mb-4">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>{downloadsError}</AlertDescription>
                    </Alert>
                  )}

                  {downloadsLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                      <span className="ml-2 text-muted-foreground">Loading download history...</span>
                    </div>
                  ) : filteredDownloads.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 gap-4">
                      <Download className="h-12 w-12 text-muted-foreground/50" />
                      <p className="text-muted-foreground text-lg">{searchTerm ? 'No downloads match your search' : 'No downloads yet.'}</p>
                    </div>
                  ) : (
                    <>
                      <div className="overflow-x-auto rounded-md border border-zinc-700">
                        <Table>
                          <TableHeader>
                            <TableRow className="border-b border-zinc-700">
                              <TableHead className="border-r border-zinc-700">Design Name</TableHead>
                              <TableHead className="border-r border-zinc-700">Service</TableHead>
                              <TableHead className="border-r border-zinc-700">Tier</TableHead>
                              <TableHead className="border-r border-zinc-700 text-right">Charged</TableHead>
                              <TableHead>Date</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {filteredDownloads.map((dl) => (
                              <TableRow key={dl.id} className="border-b border-zinc-700">
                                <TableCell className="border-r border-zinc-700 font-medium">
                                  <a
                                    href={`/services/generator?designId=${dl.designId}`}
                                    onClick={(e) => { e.preventDefault(); navigate(`/services/generator?designId=${dl.designId}`); }}
                                    style={{ color: '#FFD93D', textDecoration: 'none', cursor: 'pointer' }}
                                    onMouseEnter={e => { e.currentTarget.style.textDecoration = 'underline'; }}
                                    onMouseLeave={e => { e.currentTarget.style.textDecoration = 'none'; }}
                                  >
                                    {dl.designName}
                                  </a>
                                </TableCell>
                                <TableCell className="border-r border-zinc-700">
                                  <Badge variant="secondary">
                                    {dl.serviceName || dl.serviceCode}
                                  </Badge>
                                </TableCell>
                                <TableCell className="border-r border-zinc-700">
                                  <Badge variant={(dl as any).tierApplied === 1 ? 'default' : 'outline'}>
                                    {(dl as any).tierApplied === 1 ? 'Download' : 'Re-download'}
                                  </Badge>
                                </TableCell>
                                <TableCell className="border-r border-zinc-700 text-right font-medium text-red-400">
                                  -₹{dl.creditsCharged}
                                </TableCell>
                                <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                                  {formatDateTime(dl.createdAt)}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>

                      {/* Pagination */}
                      <div className="flex items-center justify-between pt-4">
                        <p className="text-sm text-muted-foreground">
                          Page {downloadsPage} of {downloadsTotalPages}
                        </p>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setDownloadsPage((p) => Math.max(1, p - 1))}
                            disabled={downloadsPage <= 1}
                          >
                            <ChevronLeft className="h-4 w-4 mr-1" />
                            Previous
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setDownloadsPage((p) => Math.min(downloadsTotalPages, p + 1))}
                            disabled={downloadsPage >= downloadsTotalPages}
                          >
                            Next
                            <ChevronRight className="h-4 w-4 ml-1" />
                          </Button>
                        </div>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </PageLayout.Content>


      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Design</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deleteTarget?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          {deleteError && (
            <Alert className="bg-destructive/10 border-destructive text-destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{deleteError}</AlertDescription>
            </Alert>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={deleting}
            >
              {deleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
};

export default ActivityPage;
